/**
 * Répartition des mains en apprentissage.
 *
 * Trois catégories nominales : une seule teinte, pas de légende. Barres
 * horizontales parce que « Main gauche » et « Deux mains » ne tiennent pas sous
 * une colonne.
 */

import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatCompact, formatInteger, formatPercent } from '../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS } from '../lib/theme';
import type { DataColumn, HandSplitItem } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';

const columns: ReadonlyArray<DataColumn<HandSplitItem>> = [
  { key: 'label', header: 'Main', render: (row) => row.label },
  { key: 'learns', header: 'Apprentissages', numeric: true, render: (row) => formatInteger(row.learns) },
  { key: 'share', header: 'Part', numeric: true, render: (row) => formatPercent(row.share) },
];

export function HandSplitChart({ data }: { data: readonly HandSplitItem[] }) {
  return (
    <ChartCard
      title="Répartition des mains"
      subtitle="Apprentissages dont la main jouée est renseignée"
      rows={data}
      columns={columns}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data as HandSplitItem[]} layout="vertical" margin={{ top: 4, right: 64, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={formatCompact}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fill: COLORS.textSecondary, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={100}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={<ChartTooltip valueFormatter={formatInteger} />}
          />
          <Bar
            isAnimationActive={false}
            dataKey="learns"
            name="Apprentissages"
            fill={COLORS.series1}
            radius={[0, 4, 4, 0]}
            maxBarSize={BAR_MAX_SIZE}
          >
            <LabelList
              dataKey="share"
              position="right"
              fill={COLORS.textSecondary}
              fontSize={12}
              formatter={(value: unknown) => formatPercent(Number(value))}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
